import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { ArticleView, type TakeActions } from '../components/ArticleView'
import { ReaderHeader } from '../components/reader/ReaderHeader'
import { dockClearance, ReadingControls } from '../components/reader/ReadingControls'
import { WritingFooter } from '../components/reader/WritingFooter'
import { AuthorBlock } from '../components/reader/AuthorBlock'
import { JOIN_BAR_HEIGHT, JoinBar, TakenFab } from '../components/reader/TakeBar'
import { ShareWritingSheet } from '../components/ShareWritingSheet'
import { shareExcerpt, type ShareCardContent } from '../lib/shareCard'
import { TakenDock, TakenPanel } from '../components/reader/TakenPanel'
import { useReadingLight } from '../components/reader/hooks'
import { useToast } from '../components/Toast'
import { btn, errorText, Loading, MobileSheet, Notice } from '../components/ui'
import { SIDE_PANEL_DESK } from '../components/SidePanel'
import { useSession } from '../lib/session'
import { useMediaQuery } from '../lib/hooks'
import { burrowAppUrl, publicOrigin } from '../lib/env'
import { blockText, displayName, normalizeSpace, readMinutes, shortDate, stripTags } from '../lib/text'
import {
  cardKey,
  flushQueue,
  queuedFor,
  queueTake,
  readQueue,
  sidebarCards,
  unqueueTake,
  type PendingTake,
  type QueuedTake,
  type SidebarCard,
} from '../lib/takes'
import type { PublishedPayload, PullQuoteBlock, SaveTarget, TakenCard, ThoughtBlock } from '../lib/types'
import { safePublished } from '../lib/urls'
import { quoteAttribution } from '../lib/blocks'
import { FramedImage } from '../components/FramedImage'

type State =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: PublishedPayload }

const quoteText = (b: ThoughtBlock | PullQuoteBlock) => (b.type === 'thought' ? b.text : stripTags(b.html))

/** The server's line limit (save_writing_line). */
const MAX_LINE = 2000

/** The viewer already took something (a line or the quote) from this block. */
const tookFrom = (d: PublishedPayload, blockId: string) =>
  (d.my_takes ?? []).some((t) => t.block_id === blockId) || (d.my_saves ?? []).includes(blockId)

/** Readers per block: take_counts (lines + quotes), or v1's quote saves. */
const countsOf = (d: PublishedPayload) => d.take_counts ?? d.saves ?? {}

function bumpCount(d: PublishedPayload, blockId: string, by: number): Record<string, number> {
  const counts = countsOf(d)
  return { ...counts, [blockId]: Math.max(0, (counts[blockId] ?? 0) + by) }
}

/** A card the server now has, added once (a server without my_cards shows my_takes instead). */
function withCard(d: PublishedPayload, card: TakenCard): PublishedPayload {
  if (!d.my_cards || d.my_cards.some((c) => c.thought_id === card.thought_id)) return d
  return { ...d, my_cards: [...d.my_cards, card] }
}

/** What the viewer took here, as cards: my_cards, or (from a server before cards) the takes, unsortable. */
function savedCards(d: PublishedPayload): TakenCard[] {
  if (d.my_cards) return d.my_cards
  const text = (blockId: string) => {
    const b = d.writing.blocks?.find((x) => x.id === blockId)
    return b ? blockText(b) : ''
  }
  return [
    ...(d.my_takes ?? []).map((t): TakenCard => ({ kind: 'line', block_id: t.block_id, text: t.excerpt, thought_id: '', created_at: '', collection_ids: [] })),
    ...(d.my_saves ?? []).map((b): TakenCard => ({ kind: 'quote', block_id: b, text: text(b), thought_id: '', created_at: '', collection_ids: [] })),
  ]
}

const sameIds = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((id, i) => id === b[i])

/** Where the reader's sticky header ends: the Taken drawer starts below it. */
const HEADER_HEIGHT = 65

export function ReaderPage() {
  const { handle = '', slug = '' } = useParams()
  const { backend, viewer } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const desk = useMediaQuery(SIDE_PANEL_DESK)
  const [state, setState] = useState<State>({ status: 'loading' })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [followBusy, setFollowBusy] = useState(false)
  const [readingLight, setReadingLight] = useReadingLight()
  // Takes made signed out, kept in this browser until sign-in.
  const [queue, setQueue] = useState<QueuedTake[]>(() => readQueue())
  // Signed in: takes on their way to the server, or refused (they show Retry).
  const [pending, setPending] = useState<PendingTake[]>([])
  // The Taken sidebar: the desktop drawer, or the phone sheet.
  const [panelOpen, setPanelOpen] = useState(false)
  // Something was taken here this visit: the phone's Taken button stays from then on.
  const [tookHere, setTookHere] = useState(false)
  // Desktop: a take opens the drawer as a peek, which tucks itself away (TakenDock).
  const [peeking, setPeeking] = useState(false)
  // Bumps with each take: restarts the peek, and the newest card glows.
  const [takeSeq, setTakeSeq] = useState(0)
  // The reader's collections: the full list once the sidebar opens, plus any made here.
  const [allCollections, setAllCollections] = useState<SaveTarget[] | null>(null)
  const [madeHere, setMadeHere] = useState<SaveTarget[]>([])
  // Cards (thought ids) whose move is on its way to the server.
  const [moving, setMoving] = useState<ReadonlySet<string>>(() => new Set())
  const [flushing, setFlushing] = useState(false)
  // The author's share card: opens on arrival from publishing, or from "Share as image".
  // Arriving from Publish or Update (Preview's sheet) opens it, as published or updated.
  const [shareCard, setShareCard] = useState<'published' | 'updated' | 'asked' | null>(() => {
    const arrived = location.state as { published?: boolean; updated?: boolean } | null
    return arrived?.published ? (arrived.updated ? 'updated' : 'published') : null
  })
  const flushedFor = useRef<string | null>(null)
  // Only the latest request may land: "More from…" links keep this page mounted.
  const requestSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    try {
      const data = await backend.getPublished(handle, slug)
      if (seq !== requestSeq.current) return
      // Author-supplied links and media are filtered before anything renders them.
      setState(data ? { status: 'ok', data: safePublished(data) } : { status: 'missing' })
    } catch (e) {
      if (seq !== requestSeq.current) return
      setState({ status: 'error', message: errorText(e) })
    }
  }, [backend, handle, slug])

  // A different writing starts from a clean page, not the last one's content.
  useEffect(() => {
    setState({ status: 'loading' })
    setPending([])
    setPanelOpen(false)
    setPeeking(false)
    setTookHere(false)
  }, [handle, slug])

  useEffect(() => {
    void load()
  }, [load, viewer?.id])

  // Collections are the viewer's own: start over when the viewer changes.
  useEffect(() => {
    setAllCollections(null)
    setMadeHere([])
  }, [viewer?.id])

  const data = state.status === 'ok' ? state.data : null
  const name = data ? displayName(data.author) : ''
  const signedIn = !!data?.viewer.signed_in

  useEffect(() => {
    if (!data) return
    document.title = `${data.writing.title} — ${name}`
    if (location.hash) requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'center' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.writing.id])

  const pageUrl = `${publicOrigin()}/${handle}/${slug}`

  // Keyed on the writing alone: takes patch the payload, not the writing.
  const shownWriting = data && !data.gated ? data.writing : null
  const cardContent = useMemo<ShareCardContent | null>(
    () =>
      shownWriting
        ? {
            title: shownWriting.title,
            subtitle: shownWriting.subtitle,
            author: name,
            excerpt: shareExcerpt(shownWriting.blocks ?? []),
            url: pageUrl.replace(/^https?:\/\//, ''),
            minutes: readMinutes(shownWriting.word_count ?? 0),
          }
        : null,
    [shownWriting, name, pageUrl],
  )
  const closeShareCard = () => {
    setShareCard(null)
    // Arrived from publishing: a reload shouldn't open it again.
    if ((location.state as { published?: boolean } | null)?.published) navigate(location.pathname + location.hash, { replace: true, state: null })
  }
  const signIn = () => navigate(`/signin?next=${encodeURIComponent(location.pathname + location.hash)}`)
  const join = () => navigate(`/signin?next=${encodeURIComponent(location.pathname)}`)

  const patch = (fn: (d: PublishedPayload) => PublishedPayload) =>
    setState((s) => (s.status === 'ok' ? { status: 'ok', data: fn(s.data) } : s))
  /** Patch only while the page still shows that writing: a save can land after "More from…". */
  const patchFor = (writingId: string, fn: (d: PublishedPayload) => PublishedPayload) => patch((d) => (d.writing.id === writingId ? fn(d) : d))

  // Signed in again with takes waiting from before: save them loose (never
  // into a collection), then refetch so they come back as sortable cards.
  useEffect(() => {
    if (!data || data.gated || !data.viewer.signed_in) return
    const writingId = data.writing.id
    if (flushedFor.current === writingId || queuedFor(writingId).length === 0) return
    flushedFor.current = writingId
    setFlushing(true)
    void flushQueue(backend, writingId).then(async ({ taken }) => {
      if (taken > 0) await load()
      setQueue(readQueue())
      setFlushing(false)
      if (taken > 0) toast(`Took ${taken} into your thoughts`)
    })
  }, [data, backend, load, toast, queue])

  // Sorting offers every collection, not only the page's 30 most recent.
  useEffect(() => {
    if (!panelOpen || !signedIn || allCollections) return
    let alive = true
    backend
      .listCollections()
      .then((cs) => {
        if (alive) setAllCollections(cs.map(({ id, name, cover_color }) => ({ id, name, cover_color })))
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [panelOpen, signedIn, allCollections, backend])

  const collections = useMemo(() => {
    const seen = new Set<string>()
    return [...madeHere, ...(allCollections ?? data?.viewer.collections ?? [])].filter((c) => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })
  }, [madeHere, allCollections, data?.viewer.collections])

  // What this viewer has taken here: the server's record, plus takes kept in
  // this browser (signed out, or signed in and still being flushed).
  const queued = useMemo(() => (data ? queue.filter((t) => t.writingId === data.writing.id) : []), [data, queue])
  const lines = useMemo(() => {
    const byBlock = new Map<string, string[]>()
    const add = (blockId: string, excerpt: string) => {
      const e = normalizeSpace(excerpt)
      const list = byBlock.get(blockId) ?? []
      if (e && !list.includes(e)) byBlock.set(blockId, [...list, e])
    }
    for (const t of data?.my_takes ?? []) add(t.block_id, t.excerpt)
    for (const t of queued) if (t.kind === 'line') add(t.blockId, t.excerpt)
    return byBlock
  }, [data?.my_takes, queued])
  const quotes = useMemo(
    () => new Set([...(data?.my_saves ?? []), ...queued.filter((t) => t.kind === 'quote').map((t) => t.blockId)]),
    [data?.my_saves, queued],
  )

  const cards = useMemo(
    () => (data ? sidebarCards(savedCards(data), pending, queued, !signedIn ? 'queued' : flushing ? 'saving' : 'error') : []),
    [data, pending, queued, signedIn, flushing],
  )
  // Attributed pull quotes: what's taken from one is credited to whoever said it, via this writing.
  const saidBy = useMemo(() => {
    const map = new Map<string, string>()
    for (const b of data?.writing.blocks ?? []) {
      const who = quoteAttribution(b)
      if (who) map.set(b.id, who)
    }
    return map
  }, [data?.writing.blocks])
  const kept = cards.filter((c) => c.status !== 'error')
  const takenLines = kept.filter((c) => c.kind === 'line').length

  /** Each take on a desk peeks: the drawer slides out with the new card, then tucks away. Already open, it stays as it is. */
  const noteTake = () => {
    setTookHere(true)
    if (!desk) return
    setTakeSeq((n) => n + 1)
    if (!panelOpen) {
      setPanelOpen(true)
      setPeeking(true)
    }
  }
  const openPanel = () => {
    setPanelOpen(true)
    setPeeking(false)
  }
  const closePanel = () => {
    setPanelOpen(false)
    setPeeking(false)
  }

  const putPending = (p: PendingTake) => setPending((list) => [p, ...list.filter((x) => x.key !== p.key)])
  const dropPending = (key: string) => setPending((list) => list.filter((x) => x.key !== key))

  // Signed out, a take is kept in this browser; say so once per visit.
  const explained = useRef(false)
  const keepForLater = (item: QueuedTake) => {
    setQueue(queueTake(item))
    noteTake()
    if (!explained.current) toast('Kept on this page — join Burrow to take it with you')
    explained.current = true
  }

  function takeLine(blockId: string, raw: string) {
    takePassage([{ blockId, excerpt: raw }])
  }

  /** Selected words: a line, or a passage over several paragraphs, kept as a line per block. */
  function takePassage(parts: ReadonlyArray<{ blockId: string; excerpt: string }>) {
    if (!data) return
    const clean = parts.map((p) => ({ blockId: p.blockId, excerpt: normalizeSpace(p.excerpt) })).filter((p) => p.excerpt)
    if (clean.length === 0) return
    if (clean.some((p) => p.excerpt.length > MAX_LINE)) return toast('That’s a long passage — pick a shorter line to take.')
    const at = new Date().toISOString()
    if (!data.viewer.signed_in) {
      for (const p of clean) keepForLater({ writingId: data.writing.id, blockId: p.blockId, kind: 'line', excerpt: p.excerpt, pageUrl, at })
      return
    }
    const fresh = clean.filter((p) => !lines.get(p.blockId)?.includes(p.excerpt))
    if (fresh.length === 0) return toast(clean.length === 1 ? 'You’ve already taken that line' : 'You’ve already taken that passage')
    for (const p of fresh) saveLine(p.blockId, p.excerpt, at)
  }

  /**
   * Optimistic: the highlight, the count and a "Saving…" card now. The card
   * confirms when the server answers, or offers Retry if it says no. Loose:
   * a take is never filed into a collection; the reader sorts cards one by one.
   */
  function saveLine(blockId: string, excerpt: string, at: string) {
    if (!data) return
    const writingId = data.writing.id
    const key = cardKey('line', blockId, excerpt)
    const bumped = !tookFrom(data, blockId)
    patch((d) => ({
      ...d,
      my_takes: [...(d.my_takes ?? []), { block_id: blockId, excerpt }],
      take_counts: bumped ? bumpCount(d, blockId, 1) : d.take_counts,
    }))
    putPending({ key, kind: 'line', blockId, text: excerpt, at, status: 'saving' })
    noteTake()
    backend.saveLine(writingId, blockId, excerpt, pageUrl).then(
      (thoughtId) => {
        dropPending(key)
        patchFor(writingId, (d) => withCard(d, { kind: 'line', block_id: blockId, text: excerpt, thought_id: thoughtId, created_at: at, collection_ids: [] }))
      },
      (e: unknown) => {
        patchFor(writingId, (d) => ({
          ...d,
          my_takes: (d.my_takes ?? []).filter((t) => !(t.block_id === blockId && t.excerpt === excerpt)),
          take_counts: bumped ? bumpCount(d, blockId, -1) : d.take_counts,
        }))
        putPending({ key, kind: 'line', blockId, text: excerpt, at, status: 'error', error: errorText(e) })
        toast(errorText(e))
      },
    )
  }

  function takeQuote(blockId: string) {
    if (!data) return
    const block = data.writing.blocks?.find((b) => b.id === blockId)
    const text = block ? blockText(block) : ''
    const at = new Date().toISOString()
    if (!data.viewer.signed_in) return keepForLater({ writingId: data.writing.id, blockId, kind: 'quote', excerpt: text, pageUrl, at })
    if (data.my_saves?.includes(blockId)) return
    void saveQuote(blockId, text, at)
  }

  async function saveQuote(blockId: string, text: string, at: string) {
    if (!data) return
    const writingId = data.writing.id
    const key = cardKey('quote', blockId, text)
    const bumped = !tookFrom(data, blockId)
    setBusyId(blockId)
    putPending({ key, kind: 'quote', blockId, text, at, status: 'saving' })
    noteTake()
    try {
      const thoughtId = await backend.saveQuote(writingId, blockId, pageUrl)
      const card: TakenCard = { kind: 'quote', block_id: blockId, text, thought_id: thoughtId, created_at: at, collection_ids: [] }
      dropPending(key)
      patchFor(writingId, (d) =>
        withCard(
          d.my_saves?.includes(blockId)
            ? d
            : {
                ...d,
                saves: { ...d.saves, [blockId]: (d.saves?.[blockId] ?? 0) + 1 },
                my_saves: [...(d.my_saves ?? []), blockId],
                take_counts: bumped ? bumpCount(d, blockId, 1) : d.take_counts,
              },
          card,
        ),
      )
    } catch (e) {
      putPending({ key, kind: 'quote', blockId, text, at, status: 'error', error: errorText(e) })
      toast(errorText(e))
    } finally {
      setBusyId(null)
    }
  }

  function retry(card: SidebarCard) {
    if (!data) return
    if (card.key.startsWith('q:')) {
      // Kept from before sign-in and still not saved: flush again.
      flushedFor.current = null
      setQueue(readQueue())
      return
    }
    const at = card.at ?? new Date().toISOString()
    if (card.kind === 'line') {
      if (lines.get(card.blockId)?.includes(normalizeSpace(card.text))) return dropPending(cardKey('line', card.blockId, card.text))
      saveLine(card.blockId, card.text, at)
    } else {
      if (data.my_saves?.includes(card.blockId)) return dropPending(cardKey('quote', card.blockId, card.text))
      void saveQuote(card.blockId, card.text, at)
    }
  }

  function dismiss(card: SidebarCard) {
    if (!data) return
    if (card.key.startsWith('q:')) setQueue(unqueueTake({ writingId: data.writing.id, blockId: card.blockId, kind: card.kind, excerpt: card.text }))
    else dropPending(cardKey(card.kind, card.blockId, card.text))
  }

  /** Sort one card, and only that card: optimistic, rolled back with a toast if the server says no. */
  function sortCard(card: SidebarCard, collectionId: string | null) {
    if (!data || !card.thoughtId) return
    const thoughtId = card.thoughtId
    const writingId = data.writing.id
    const before = card.collectionIds
    const after = collectionId ? [collectionId] : []
    if (sameIds(before, after)) return
    const setIds = (ids: string[]) =>
      patchFor(writingId, (d) => ({ ...d, my_cards: d.my_cards?.map((c) => (c.thought_id === thoughtId ? { ...c, collection_ids: ids } : c)) }))
    setIds(after)
    setMoving((m) => new Set(m).add(thoughtId))
    backend
      .refileTakenThought(thoughtId, collectionId)
      .catch((e: unknown) => {
        setIds(before)
        toast(errorText(e))
      })
      .finally(() =>
        setMoving((m) => {
          const next = new Set(m)
          next.delete(thoughtId)
          return next
        }),
      )
  }

  /** "New collection…": make it, then sort the card into it. */
  async function createAndSort(card: SidebarCard, collectionName: string): Promise<boolean> {
    try {
      const c = await backend.createCollection(collectionName)
      setMadeHere((list) => [{ id: c.id, name: c.name, cover_color: c.cover_color }, ...list])
      sortCard(card, c.id)
      return true
    } catch (e) {
      toast(errorText(e))
      return false
    }
  }

  async function copy(text: string, done: string) {
    try {
      await navigator.clipboard.writeText(text)
      toast(done)
    } catch {
      toast('Couldn’t copy — select the text instead.')
    }
  }

  async function share(block?: ThoughtBlock | PullQuoteBlock) {
    if (!data) return
    const url = block ? `${pageUrl}#q-${block.id}` : pageUrl
    if (navigator.share) {
      try {
        await navigator.share({ title: data.writing.title, text: block ? `“${quoteText(block)}”` : data.writing.subtitle || undefined, url })
      } catch {
        /* dismissed */
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast('Link copied')
    } catch {
      toast(url)
    }
  }

  const takes: TakeActions | undefined = data
    ? {
        lines,
        quotes,
        counts: countsOf(data),
        busyId,
        takeLine,
        takePassage,
        takeQuote,
        copyLine: (excerpt) => void copy(`“${excerpt}”\n— ${name}, ${data.writing.title}\n${pageUrl}`, 'Line copied'),
        copyQuote(block) {
          const said = quoteAttribution(block)
          const source =
            block.type === 'thought' && block.source?.title
              ? block.source.title
              : said
                ? `${said}, via ${name}, ${data.writing.title}`
                : `${name}, ${data.writing.title}`
          void copy(`“${quoteText(block)}”\n— ${source}\n${pageUrl}#q-${block.id}`, 'Quote copied')
        },
        shareQuote: (block) => void share(block),
      }
    : undefined

  async function toggleFollow() {
    if (!data) return
    if (!data.viewer.signed_in) return signIn()
    setFollowBusy(true)
    try {
      await backend.setFollowing(data.author.id, !data.viewer.follows)
      if (data.gated) await load()
      else patch((d) => ({ ...d, viewer: { ...d.viewer, follows: !d.viewer.follows } }))
    } catch (e) {
      toast(errorText(e))
    } finally {
      setFollowBusy(false)
    }
  }

  if (state.status === 'loading') return <Loading label="Opening the page…" />
  if (state.status === 'missing') {
    return (
      <Notice
        title="This writing isn’t here"
        body="It may have been unpublished, or the link has changed."
        action={
          <a href={burrowAppUrl} className="font-display text-[1.3rem] font-medium italic text-primary no-underline">
            Burrow
          </a>
        }
      />
    )
  }
  if (state.status === 'error') return <Notice title="Couldn’t load this writing" body={state.message} />
  const d = state.data

  const headerProps = { onShare: () => void share() }

  if (d.gated) {
    return (
      <div className="min-h-dvh bg-canvas">
        <ReaderHeader {...headerProps} />
        <ReadingControls clearance={dockClearance({ desk })} />
        <main className="px-4 pb-24 pt-10 sm:pt-[52px]">
          <div className="wr-column flex flex-col font-serif">
            {d.writing.cover_url && (
              <FramedImage src={d.writing.cover_url} frame={d.writing.cover_frame} className="mb-8 h-[220px] w-full rounded-2xl sm:mb-9 sm:h-[320px]" />
            )}
            <h1 className="wr-t-title m-0 mb-3">{d.writing.title}</h1>
            {d.writing.subtitle && (
              <p className="wr-t-subtitle m-0 mb-[22px]">{d.writing.subtitle}</p>
            )}
            <div className="mt-4 flex flex-col items-start gap-3 rounded-xl border border-border/40 bg-background p-6 font-sans">
              <span className="flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">
                <span aria-hidden className="size-1.5 rounded-full bg-primary" />
                THINKING ALONG WITH {name.toUpperCase()}
              </span>
              <p className="m-0 font-display text-[21px]/[1.3] text-primary">This writing is for those Thinking Along with {name}.</p>
              <p className="m-0 text-[13.5px] text-muted-foreground">
                {d.viewer.signed_in ? `Think Along with ${name} to read it.` : `Sign in and Think Along with ${name} to read it.`}
              </p>
              <button type="button" onClick={toggleFollow} disabled={followBusy} className={`${btn.primaryLg} mt-1`}>
                {d.viewer.signed_in ? `Think Along with ${name}` : 'Sign in to read'}
              </button>
            </div>
          </div>
        </main>
        <WritingFooter />
      </div>
    )
  }

  const w = d.writing
  const panel = (
    <TakenPanel
      writingTitle={w.title}
      author={name}
      signedIn={d.viewer.signed_in}
      cards={cards}
      saidBy={saidBy}
      collections={collections}
      moving={moving}
      onSort={sortCard}
      onCreate={createAndSort}
      onRetry={retry}
      onDismiss={dismiss}
      onJoin={join}
      onClose={closePanel}
      fresh={desk ? takeSeq : 0}
    />
  )
  return (
    <div className="min-h-dvh bg-canvas">
      <ReaderHeader {...headerProps} />
      <main className="overflow-x-clip px-4 pt-10 sm:pt-[52px]">
        <ArticleView
          title={w.title}
          subtitle={w.subtitle}
          coverUrl={w.cover_url}
          coverFrame={w.cover_frame ?? null}
          blocks={w.blocks ?? []}
          showTimes={!!w.show_times}
          showSources={w.show_sources !== false}
          author={{ name, avatarUrl: d.author.avatar_url }}
          date={w.published_at ?? new Date().toISOString()}
          wordCount={w.word_count ?? 0}
          narration={w.narration ?? null}
          readingLight={readingLight}
          takes={takes}
        >
          {d.collection && (
            <CollectionCard
              name={d.collection.name}
              color={d.collection.cover_color}
              count={d.collection.thought_count}
              author={name}
              href={d.collection.public_slug ? `${burrowAppUrl}/c/${d.collection.public_slug}` : null}
            />
          )}

          {d.more && d.more.length > 0 && (
            <section aria-labelledby="more-heading" className="mt-10 font-sans">
              <h2 id="more-heading" className="m-0 mb-3.5 text-[11.5px] font-semibold tracking-[0.06em] text-muted-foreground">
                MORE FROM {name.toUpperCase()}
              </h2>
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
                {d.more.map((m, i) => (
                  <Link key={m.id} to={`/${handle}/${m.slug}`} className="group flex flex-col gap-2 text-foreground no-underline">
                    {m.cover_url ? (
                      <FramedImage src={m.cover_url} frame={m.cover_frame} className="h-[110px] w-full rounded-xl" />
                    ) : (
                      <div aria-hidden className={`h-[110px] rounded-xl ${MORE_GRADIENTS[i % MORE_GRADIENTS.length]}`} />
                    )}
                    <span className="font-display text-[17px]/[1.25] text-primary group-hover:text-accent">{m.title}</span>
                    <span className="text-[11.5px] font-medium text-muted-foreground">
                      {shortDate(m.published_at)} · {readMinutes(m.word_count)} min
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <AuthorBlock name={name} avatarUrl={d.author.avatar_url} count={d.author.published_count}>
            {d.viewer.is_owner ? (
              <>
                <button type="button" onClick={() => setShareCard('asked')} className={btn.outlineLg}>
                  Share as image
                </button>
                <Link to={`/w/${d.writing.id}`} className={`${btn.outlineLg} no-underline`}>
                  Edit
                </Link>
              </>
            ) : (
              <button
                type="button"
                disabled={followBusy}
                onClick={toggleFollow}
                aria-pressed={d.viewer.follows}
                className={d.viewer.follows ? btn.outlineLg : btn.primaryLg}
              >
                {d.viewer.follows ? 'Thinking Along' : 'Think Along'}
              </button>
            )}
          </AuthorBlock>
        </ArticleView>
      </main>
      {/* On phones its foot clears the Join bar and Taken button fixed at the bottom. */}
      <WritingFooter clearBottomBar={!desk} />
      {/* The count is the confirmation for a take (no toast on success). */}
      <p className="sr-only" aria-live="polite">
        {kept.length > 0 ? `${kept.length} taken` : ''}
      </p>
      {!(panelOpen && !desk) && (
        <ReadingControls
          readingLight={{ on: readingLight, onChange: setReadingLight }}
          clearance={dockClearance({
            desk,
            takenPill: true,
            drawerOpen: panelOpen,
            joinBar: signedIn ? 0 : JOIN_BAR_HEIGHT,
            takenFab: cards.length > 0 || tookHere,
          })}
        />
      )}
      {shareCard && d.viewer.is_owner && cardContent && (
        <ShareWritingSheet
          content={cardContent}
          url={pageUrl}
          justPublished={shareCard !== 'asked'}
          updated={shareCard === 'updated'}
          onClose={closeShareCard}
        />
      )}
      {desk ? (
        <TakenDock
          open={panelOpen}
          peek={peeking}
          peekSeq={takeSeq}
          count={kept.length}
          lines={takenLines}
          quotes={kept.length - takenLines}
          top={HEADER_HEIGHT}
          onOpen={openPanel}
          onClose={closePanel}
          onPin={() => setPeeking(false)}
        >
          {panel}
        </TakenDock>
      ) : (
        <>
          {!signedIn && <JoinBar taken={kept.length > 0} onJoin={join} />}
          {(cards.length > 0 || tookHere) && !panelOpen && <TakenFab count={kept.length} overBar={!signedIn} onOpen={() => setPanelOpen(true)} />}
          {panelOpen && (
            <MobileSheet label="Taken" sidebar onClose={closePanel}>
              {panel}
            </MobileSheet>
          )}
        </>
      )}
    </div>
  )
}

// Covers for "More from" writings without a photo: flat Burrow tones.
const MORE_GRADIENTS = ['bg-muted', 'bg-secondary', 'bg-accent/15']

function CollectionCard({ name, color, count, author, href }: { name: string; color: string | null; count: number; author: string; href: string | null }) {
  const body = (
    <>
      <span aria-hidden className="size-16 shrink-0 rounded-xl" style={{ background: color || 'var(--accent)' }} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">COLLECTION BEHIND THIS WRITING</span>
        <span className="font-display text-[20px]/[1.2] text-primary">{name}</span>
        <span className="text-[13px] text-muted-foreground">
          {count} thought{count === 1 ? '' : 's'} {author} gathered while writing this
        </span>
      </span>
      {href && <span className={`${btn.outlineLg}`}>Explore</span>}
    </>
  )
  const cls = 'mt-8 flex flex-wrap items-center gap-[18px] rounded-xl border border-card-edge bg-card px-5 py-[18px] font-sans text-foreground no-underline sm:flex-nowrap'
  return href ? (
    <a href={href} className={`${cls} transition-colors hover:border-accent`}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  )
}
